import { useState, useEffect } from "react";
import { getPortalSupabase } from "@/lib/portalSupabase";
import { useLocation } from "wouter";
import { Loader2 } from "lucide-react";

interface PortalAuthGuardProps {
  children: React.ReactNode;
  /** Pass false for the account picker page itself to avoid a redirect loop. */
  requireAccount?: boolean;
}

export function PortalAuthGuard({ children, requireAccount = true }: PortalAuthGuardProps) {
  // Staff portal preview rides the staff session — skip the portal auth checks entirely.
  const isPreview = localStorage.getItem("portal_preview") === "true";
  const [checking, setChecking] = useState(!isPreview);
  const [authed, setAuthed] = useState(isPreview);
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (isPreview) return;
    getPortalSupabase().then(async (supabase) => {
      let { data: { session } } = await supabase.auth.getSession();

      // Access token may be expired but refresh token still valid — try refreshing before giving up.
      if (!session) {
        const { data: refreshed } = await supabase.auth.refreshSession();
        session = refreshed.session;
      }

      if (!session) {
        setLocation("/portal/login");
        setChecking(false);
        return;
      }

      // If this page needs an account context and it's not in localStorage (e.g. browser reopen),
      // fetch the user's accounts and either auto-set or redirect to the picker.
      if (requireAccount && !localStorage.getItem("portal_account_id")) {
        try {
          const res = await fetch("/api/portal/accounts", {
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (!res.ok) {
            setLocation("/portal/login");
            setChecking(false);
            return;
          }
          const accounts: Array<{ account_id: string; account_name: string }> = await res.json();
          if (accounts.length === 0) {
            setLocation("/portal/login");
            setChecking(false);
            return;
          }
          if (accounts.length === 1) {
            localStorage.setItem("portal_account_id", accounts[0].account_id);
          } else {
            setLocation("/portal/pick-account");
            setChecking(false);
            return;
          }
        } catch {
          setLocation("/portal/login");
          setChecking(false);
          return;
        }
      }

      setAuthed(true);
      setChecking(false);
    });
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return authed ? <>{children}</> : null;
}
